import { useEffect } from 'react';
import { Navbar, Footer } from '../components/Layout';
import EmailSignupForm from '../components/growth/EmailSignupForm';
import { CheckCircle2, Sparkles, Users, School, Handshake } from 'lucide-react';

const BENEFITS = [
  ['Early access', 'Be first into the platform as we open seats by cohort.'],
  ['Launch notification', "We'll tell you the moment the platform officially launches."],
  ['Guides and research', 'Practical guides on careers, college, trades, aid and AI.'],
  ['Demos and pilots', 'Invitations to live demonstrations, school pilots and workshops.'],
];

const AUDIENCES = [
  [Users, 'Students & families', 'Understand yourself, explore real pathways, build a plan you can actually execute.'],
  [School, 'Schools & districts', 'Give every student a personalised, evidence-backed pathway plan counselors can see.'],
  [Handshake, 'Partners & sponsors', 'Back a workforce-readiness system that reaches students where decisions get made.'],
];

export default function WaitlistPage() {
  useEffect(() => {
    document.title = 'Join the Waitlist | Agentic FuturePath AI';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content',
      'Join the Agentic FuturePath AI waitlist for early access to AI-powered career, college, trades and financial planning for students.');
  }, []);

  return (
    <div className="min-h-screen bg-navy-gradient">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-cyan-300 font-semibold inline-flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> Early access
            </p>
            <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mt-3">
              Get on the <span className="bg-gradient-to-r from-cyan-300 to-blue-400 bg-clip-text text-transparent">FuturePath</span> list
            </h1>
            <p className="text-base text-slate-300 mt-4 max-w-xl">
              Agentic FuturePath AI helps young people understand themselves, explore realistic pathways, and build an
              actionable plan for their future, then coaches them while they execute it. Join the waitlist and we'll
              keep you in the loop as we open access.
            </p>

            <ul className="mt-8 space-y-3">
              {BENEFITS.map(([title, text]) => (
                <li key={title} className="flex gap-3" data-testid={`waitlist-benefit-${title.toLowerCase().replace(/\s+/g, '-')}`}>
                  <CheckCircle2 className="w-5 h-5 text-cyan-300 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-white">{title}</p>
                    <p className="text-sm text-slate-300">{text}</p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-10">
              {AUDIENCES.map(([Icon, title, text]) => (
                <div key={title} className="rounded-xl border border-slate-700/70 bg-slate-900/40 p-4">
                  <Icon className="w-5 h-5 text-cyan-300" />
                  <p className="text-sm font-semibold text-white mt-2">{title}</p>
                  <p className="text-xs text-slate-300 mt-1">{text}</p>
                </div>
              ))}
            </div>
          </div>

          <EmailSignupForm variant="full" source="waitlist_page"
            heading="Join the waitlist"
            sub="Tell us who you are so we send you the right things. Two fields are required, the rest helps us tailor it." />
        </div>
      </div>
      <Footer />
    </div>
  );
}
